import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { RatePlanFindInterface } from '@zaoblako/shared/modules/saas/dtos/rate-plan/rate-plan-find.dto';
import { RatePlanFormDto, type RatePlanFormInterface } from '@zaoblako/shared/modules/saas/dtos/rate-plan/rate-plan.form.dto';
import type { ModuleInterface } from '@zaoblako/shared/modules/security/dtos/access-control/module.dto';
import { BasisRatePlanService } from '@zaoblako/web/core/services/basis/basis-rate-plan.service';
import { BasisSystemAppMetadataService } from '@zaoblako/web/core/services/basis/basis-system-app-metadata.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-rate-plan-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    EditorComponent,
    InputNumberModule,
    FormsModule,
    ButtonModule,
    MultiSelectComponent,
    CheckboxModule
  ],
  templateUrl: './rate-plan-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RatePlanFormComponent extends BaseFormComponent<RatePlanFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public form = <RatePlanFormInterface>{};
  public dto = RatePlanFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public engine: 'class-transformer' | 'serde' = 'serde';

  public modules: ModuleInterface[] = [];
  public selectedModules: ModuleInterface[] = [];

  public constructor(
    private readonly basisRatePlanService: BasisRatePlanService,
    private readonly formStoreService: FormStoreService,
    private readonly basisSystemAppMetadataService: BasisSystemAppMetadataService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.basisSystemAppMetadataService.getModules().subscribe({
      next: (modules) => {
        this.modules = modules;
        this.cdRef.markForCheck();
      }
    });

    this.form.isDefault = false;

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.basisRatePlanService
      .findById<RatePlanFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (item) => {
          this.form = item;
          this.selectedModules = this.modules.filter((module) =>
            item.availableModules.some((m) => m === module.systemName)
          );
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public changeModules(modules: ModuleInterface[]) {
    this.form.availableModules = modules.map((module) => module.systemName);
  }

  public saveItem() {
    if (this.checkValidate(this.form).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisRatePlanService.update<RatePlanFormInterface, RatePlanFindInterface>(this.form).subscribe({
          next: (ratePlan) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Тариф "${this.form?.name}" обновлен`);
            this.formStoreService.saveNext(ratePlan, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisRatePlanService.create<RatePlanFindInterface>(this.form).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Тариф создан');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/admin/rate-plan/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteRatePlan() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить тариф "${this.form?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisRatePlanService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Тариф "${this.form?.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }
}
